import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Gamepad2, BarChart3, BookOpen, Settings, Target } from 'lucide-react'
import ParentGameStats from './GameStats'
import ParentSpellingLists from './SpellingLists'
import ParentSpellingTracker from './SpellingTracker'
import GameManager from './GameManager'

// Lazy-import GameArcade - same component used by student portal
import StudentGameArcade from '../student/GameArcade'

type Tab = 'arcade' | 'stats' | 'tracker' | 'spelling' | 'manage'

export default function GamesHub() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as Tab | null
  const [activeTab, setActiveTab] = useState<Tab>(tabParam || 'arcade')

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
    setSearchParams(tab === 'arcade' ? {} : { tab })
  }

  const tabs = [
    { id: 'arcade' as Tab, label: 'Arcade', icon: Gamepad2 },
    { id: 'stats' as Tab, label: 'Stats', icon: BarChart3 },
    { id: 'tracker' as Tab, label: 'Spelling Tracker', icon: Target },
    { id: 'spelling' as Tab, label: 'Spelling Lists', icon: BookOpen },
    { id: 'manage' as Tab, label: 'Manage', icon: Settings },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <Gamepad2 className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Games</h1>
          <p className="text-sm text-gray-400">Play, track stats, and manage spelling lists</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex bg-surface-card rounded-lg p-1 gap-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => handleTabChange(id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-surface-hover'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'arcade' && <StudentGameArcade />}
      {activeTab === 'stats' && <ParentGameStats />}
      {activeTab === 'tracker' && <ParentSpellingTracker />}
      {activeTab === 'spelling' && <ParentSpellingLists />}
      {activeTab === 'manage' && <GameManager />}
    </div>
  )
}
